import { z } from 'zod';
import type { CardId, PlayerId } from '../engine/types';
import { DECK_SIZE } from '../engine/deck-rules';

export const DECK_STORAGE_KEY = 'towerarena.com.decks';

export interface DeckSelection {
  schemaVersion: 1;
  player: readonly CardId[];
  bot: readonly CardId[];
}

export interface ParsedDeckSelection {
  selection: DeckSelection;
  repaired: boolean;
}

const deckSchema = z.array(z.string().min(1)).length(DECK_SIZE).refine((deck) => new Set(deck).size === DECK_SIZE, 'Deck cards must be unique');
const savedDeckSchema = z.object({ schemaVersion: z.literal(1), player: deckSchema, bot: deckSchema });

const cloneDefaults = (defaults: Readonly<Record<PlayerId, readonly CardId[]>>): DeckSelection => ({
  schemaVersion: 1,
  player: [...defaults.player],
  bot: [...defaults.bot],
});

export function parseSavedDeckSelection(
  raw: string | null,
  availableCardIds: ReadonlySet<string>,
  defaults: Readonly<Record<PlayerId, readonly CardId[]>>,
): ParsedDeckSelection {
  if (!raw) return { selection: cloneDefaults(defaults), repaired: false };
  try {
    const parsed = savedDeckSchema.parse(JSON.parse(raw));
    if ([...parsed.player, ...parsed.bot].some((cardId) => !availableCardIds.has(cardId))) return { selection: cloneDefaults(defaults), repaired: true };
    return {
      selection: { schemaVersion: 1, player: parsed.player as CardId[], bot: parsed.bot as CardId[] },
      repaired: false,
    };
  } catch {
    return { selection: cloneDefaults(defaults), repaired: true };
  }
}

export function saveDeckSelection(storage: Pick<Storage, 'setItem'>, selection: DeckSelection): boolean {
  try {
    storage.setItem(DECK_STORAGE_KEY, JSON.stringify(selection));
    return true;
  } catch {
    return false;
  }
}
